import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import SalesChart from './SalesChart';
import ProductDistributionChart from './ProductDistributionChart';
import PerformanceMetricsChart from './PerformanceMetricsChart';
import MonthlyRevenueChart from './MonthlyRevenueChart';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ChartType } from './BaseChart';

interface AIFeatureAnalyticsProps {
  featureId?: string;
}

const AIFeatureAnalytics: React.FC<AIFeatureAnalyticsProps> = ({ featureId }) => {
  const [usageChartType, setUsageChartType] = useState<ChartType>('line');
  const [distributionChartType, setDistributionChartType] = useState<ChartType>('pie');
  const [performanceChartType, setPerformanceChartType] = useState<ChartType>('radar');
  const [revenueChartType, setRevenueChartType] = useState<ChartType>('bar');

  // Example data - in a real application, this would come from your API
  const usageData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    values: [120, 150, 180, 220, 280, 320],
  };

  const featureDistributionData = {
    labels: ['Smart Message Optimization', 'Sentiment Analysis', 'Language Detection', 'Content Generation', 'Spam Detection'],
    values: [35, 25, 15, 20, 5],
  };

  const performanceData = {
    labels: ['Accuracy', 'Speed', 'Reliability', 'Cost Efficiency', 'User Satisfaction', 'Integration'],
    values: [92, 88, 95, 85, 90, 87],
  };

  const revenueData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    values: [2500, 3200, 3800, 4200, 4800, 5500],
  };

  // Chart customization options
  const usageChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: 'Usage Count',
        },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  const revenueChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
        },
        ticks: {
          callback: (value: number) => `$${value.toLocaleString()}`,
        },
        title: {
          display: true,
          text: 'Revenue ($)',
        },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  const handleExportCSV = (data: { labels: string[]; values: number[] }, filename: string) => {
    const csvContent = [
      ['Label', 'Value'],
      ...data.labels.map((label, index) => [label, data.values[index]]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
  };

  const handleExportPDF = (chartId: string, filename: string) => {
    const canvas = document.getElementById(chartId) as HTMLCanvasElement;
    if (canvas) {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${filename}.pdf`);
    }
  };

  const ChartTypeSelect = ({ value, onChange }: { value: ChartType; onChange: (type: ChartType) => void }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ChartType)}
      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
    >
      <option value="line">Line Chart</option>
      <option value="bar">Bar Chart</option>
      <option value="pie">Pie Chart</option>
      <option value="doughnut">Doughnut Chart</option>
      <option value="radar">Radar Chart</option>
      <option value="polarArea">Polar Area Chart</option>
    </select>
  );

  const ExportButtons = ({ data, chartId, filename }: { data: { labels: string[]; values: number[] }; chartId: string; filename: string }) => (
    <div className="flex space-x-2 mt-2">
      <button
        onClick={() => handleExportCSV(data, filename)}
        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
        Export CSV
      </button>
      <button
        onClick={() => handleExportPDF(chartId, filename)}
        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
        Export PDF
      </button>
    </div>
  );

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6">AI Feature Analytics</h2>
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Feature Usage Trends</h3>
            <ChartTypeSelect value={usageChartType} onChange={setUsageChartType} />
          </div>
          <SalesChart 
            data={usageData} 
            title="Feature Usage Trends"
            options={usageChartOptions}
            type={usageChartType}
          />
          <ExportButtons data={usageData} chartId="usageChart" filename="feature-usage" />
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Feature Distribution</h3>
            <ChartTypeSelect value={distributionChartType} onChange={setDistributionChartType} />
          </div>
          <ProductDistributionChart 
            data={featureDistributionData}
            title="Feature Distribution"
            type={distributionChartType}
          />
          <ExportButtons data={featureDistributionData} chartId="distributionChart" filename="feature-distribution" />
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Feature Performance Metrics</h3>
            <ChartTypeSelect value={performanceChartType} onChange={setPerformanceChartType} />
          </div>
          <PerformanceMetricsChart 
            data={performanceData}
            title="Feature Performance Metrics"
            type={performanceChartType}
            options={{
              scales: {
                r: {
                  min: 0,
                  max: 100,
                  ticks: {
                    stepSize: 20,
                  },
                },
              },
            }}
          />
          <ExportButtons data={performanceData} chartId="performanceChart" filename="feature-performance" />
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Feature Revenue</h3>
            <ChartTypeSelect value={revenueChartType} onChange={setRevenueChartType} />
          </div>
          <MonthlyRevenueChart 
            data={revenueData}
            title="Feature Revenue"
            type={revenueChartType}
            options={revenueChartOptions}
          />
          <ExportButtons data={revenueData} chartId="revenueChart" filename="feature-revenue" />
        </div>
      </div>
    </div>
  );
};

export default AIFeatureAnalytics; 